import { useState } from 'react';
import { Play } from 'lucide-react';
import {
  Button,
  Card,
  DataTable,
  ErrorState,
  FilterBar,
  PanelTitle,
  Stat,
  StatusPill,
  TextField,
  type DataTableColumn,
} from '../../components';
import { formatCents, formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { stringParam } from '../profiles/people';
import type { ReconciliationLine } from './api';
import { useRunReconciliation } from './queries';

function yesterdayUtc(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

const COLUMNS: DataTableColumn<ReconciliationLine>[] = [
  {
    id: 'order',
    header: 'Order',
    width: '1.2fr',
    cell: (l) => <span className="truncate font-mono text-[12.5px]">{l.orderId}</span>,
  },
  {
    id: 'charge',
    header: 'Charge',
    width: '110px',
    align: 'right',
    numeric: true,
    cell: (l) => formatCents(l.chargeTotalCents),
  },
  {
    id: 'estFee',
    header: 'Est. fee',
    width: '100px',
    align: 'right',
    numeric: true,
    cell: (l) => formatCents(l.estimatedStripeFeeCents),
  },
  {
    id: 'fee',
    header: 'Stripe fee',
    width: '100px',
    align: 'right',
    numeric: true,
    cell: (l) =>
      l.reportedStripeFeeCents === undefined ? (
        <span className="text-muted">—</span>
      ) : (
        formatCents(l.reportedStripeFeeCents)
      ),
  },
  {
    id: 'payout',
    header: 'Payout',
    width: '1.2fr',
    cell: (l) => <span className="truncate font-mono text-[12.5px] text-muted">{l.stripePayoutId ?? '—'}</span>,
  },
  {
    id: 'state',
    header: 'State',
    width: '1.6fr',
    wrap: true,
    cell: (l) =>
      l.unreconciled ? (
        <span className="text-[13px]">
          <StatusPill tone="danger" dot>
            Unreconciled
          </StatusPill>
          {l.reason && <span className="mt-0.5 block text-xs text-muted">{l.reason}</span>}
        </span>
      ) : (
        <StatusPill tone="success" dot>
          Carried
        </StatusPill>
      ),
  },
];

/**
 * Reconciliation (§7.10): one settlement day's charges, less Stripe fees,
 * against what Stripe paid out. Runs only on "Run" — a day that does not tie
 * raises alert #8, so it must never run on page load or refresh.
 */
export function ReconciliationScreen() {
  const table = useTableUrlState();
  const [date, setDate] = useState(stringParam(table.filters.date) ?? yesterdayUtc());
  const run = useRunReconciliation();
  const r = run.data;

  return (
    <div className="flex flex-col gap-[18px]">
      <FilterBar>
        <label className="text-[13px] text-muted" htmlFor="recon-date">
          Settlement day (UTC)
        </label>
        <TextField
          id="recon-date"
          type="date"
          surface="white"
          containerClassName="w-[180px]"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <Button
          size="sm"
          disabled={!date}
          loading={run.isPending}
          onClick={() => {
            table.setParams({ date });
            run.mutate(date);
          }}
        >
          <Play size={14} aria-hidden="true" /> Run
        </Button>
        <span className="text-xs text-muted">A day that doesn't tie raises a reconciliation alert.</span>
      </FilterBar>

      {run.error && <ErrorState error={run.error} onRetry={() => run.mutate(date)} />}

      {!r && !run.isPending && !run.error && (
        <Card surface="panel">
          <p className="m-0 text-sm text-muted">Choose a settlement day and press Run.</p>
        </Card>
      )}

      {r && (
        <>
          <Card surface="panel">
            <PanelTitle
              eyebrow={`Settlement ${r.settlementDate ?? date}`}
              actions={
                <StatusPill tone={r.ties ? 'success' : r.transactionCount === 0 ? 'neutral' : 'danger'} dot>
                  {r.transactionCount === 0 ? 'Nothing settled' : r.ties ? 'Ties' : 'Does not tie'}
                </StatusPill>
              }
            >
              {formatCount(r.transactionCount)} transactions
            </PanelTitle>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2.5">
              <Stat label="Charges" value={formatCents(r.chargeTotalCents)} />
              <Stat
                label="Stripe fees"
                value={formatCents(r.reportedStripeFeeCents)}
                hint={`est. ${formatCents(r.estimatedStripeFeeCents)}`}
              />
              <Stat label="Expected net" value={formatCents(r.expectedNetCents)} />
              <Stat
                label="Paid out"
                value={formatCents(r.payoutTotalCents)}
                hint={r.payoutIds?.length ? `${r.payoutIds.length} payouts` : undefined}
              />
              <Stat label="Variance" value={formatCents(r.varianceCents)} />
              <Stat label="Unreconciled" value={formatCount(r.unreconciledCount)} />
            </div>
          </Card>
          <DataTable
            label="Reconciliation lines"
            minWidth={980}
            columns={COLUMNS}
            rows={[...(r.lines ?? [])].sort((a, b) => Number(b.unreconciled) - Number(a.unreconciled))}
            getRowId={(l) => l.snapshotId}
            emptyLabel="No transactions settled that day."
          />
        </>
      )}
    </div>
  );
}
